// C:\projects\mapweb-repo\map\polygon-map\src\features\userTokenManagementSlice.ts
import { RootState } from '../app/store'
import { createSlice, PayloadAction, createAsyncThunk, Dispatch } from '@reduxjs/toolkit';
// NOTE: You will need to ensure 'storage' and 'CustomSession' are correctly imported from your utils file.
import { storage, CUSTOM_METHOD, HOSTED_UI_REDIRECT, } from '../app/auth/utils';

// 🛑 IMPORTANT: Replace 'oidcApi' with your actual RTK Query service name
import { oidcApi } from './oidcSlice';



// // --- Interface Definitions ---
// interface TokenPayload {
//     sub: string;
//     id_token?: string;
//     access_token?: string;
//     expired?: boolean
//     profileId?: number
//     expires_at: number
//     // It is highly recommended to include expires_at here for consistency
// }

export interface CustomSession {
  id_token?: string;
  access_token?: string;
  refresh_token?: string;
  expires_at?: number; // Unix timestamp for access token expiry
  profile: any; // Decoded user profile/claims
  sub: string
}



interface UserTokenManagementState {
  //tokenPayload: TokenPayload | null;
  session?: CustomSession | null | undefined,
  isAuthReady: boolean;
  isAuthenticated: boolean,
  authMethod: typeof CUSTOM_METHOD | typeof HOSTED_UI_REDIRECT | undefined
  // True once MagicLinkGuard detects a ?t= magic-link token at first mount.
  // Stable for the lifetime of the page; doesn't flip back when the URL is
  // later cleaned. Consumers couple this with a login-success signal to
  // decide whether they're still waiting for magic-link auth.
  isMagicLogin: boolean;
  // isRefreshingToken: boolean; // Optional loading indicator
}
const session = storage.loadSession();
const initialState: UserTokenManagementState = {
  //tokenPayload: null,
  session: session,//storage.loadSession(),//null,
  isAuthenticated: !!session,
  isAuthReady: !!session,
  authMethod: storage.isCustomFlowActive() ? CUSTOM_METHOD : undefined,
  isMagicLogin: false,
};

// Define the shape of the global state for correct typing in the thunk
// NOTE: You must export 'store' from your store setup file for this to work.
// import { store } from '../app/store'; 
// type RootState = ReturnType<typeof store.getState>;


// --- Thunk Definition (Corrected for Cookie-Based Renewal) ---
//only needed for proactive renewal wich we are not doing
export const checkAndRefreshAccessToken = createAsyncThunk(
  'userTokenManagement/checkAndRefresh',
  // We use generic types for Dispatch and getState for maximum compatibility
  async (forceRefetch: boolean, { getState, dispatch }) => {
    const session = storage.loadSession() as CustomSession | null;

    // 1. Check for required data
    if (!session /*|| !session.refresh_token */ || !session.expires_at) {
      //leodispatch(userTokenManagementSlice.actions.clearSessionRTK());
      return null;
    }

    const expiresAtMs = session.expires_at * 1000;
    const REFRESH_BUFFER_MS = 5000; // Renew if expiring within 5 seconds

    // 2. Check token expiry
    if (!forceRefetch && expiresAtMs > Date.now() + REFRESH_BUFFER_MS) {
      return session.access_token; // Still valid
    }

    console.log('Access token nearing expiry. Triggering renewal via Thunk dispatch...');

    try {
      // 3. Dispatch RTK Query without arguments (as refresh token is in a cookie)
      const result = await dispatch(
        oidcApi.endpoints.renewSms.initiate(undefined, { forceRefetch: true })
      ).unwrap();

      if (!result?.access_token) {
        throw new Error("Renewal failed to return a new access token.");
      }

      // 4. Renewal successful: Update storage and dispatch success action
      // const newSessionPayload: TokenPayload = {
      //     sub: session.profile.sub,
      //     id_token: result.id_token, 
      //     access_token: result.access_token,
      //     expired: false,
      //     //profileId: session.profileId
      // };

      const newSession: CustomSession = {
        ...session,
        // sub: session.sub,
        // id_token: result.id_token, 
        // access_token: result.access_token,
        expires_at: result.expires_at
        //expired: false,
        //profileId: session.profileId
      };
      // NOTE: Must merge the new tokens with old data (like profile) before saving to persistence
      storage.saveSession({ ...session, ...result });
      dispatch(userTokenManagementSlice.actions.setSessionForRTK({ newSession, authMethod: CUSTOM_METHOD }));

      return newSession.access_token;

    } catch (error) {
      // 5. Renewal failed: Clear storage and dispatch cleanup action
      console.error('Failed to refresh token via Thunk. Refresh token may be expired:', error);
      dispatch(userTokenManagementSlice.actions.clearSessionRTK());
      storage.clearSession();
      return null;
    }
  }
);


// --- Slice Definition with All Reducers ---
export const userTokenManagementSlice = createSlice({
  name: 'userTokenManagement',
  initialState,

  reducers: {
    setSessionForRTK: (state, action: PayloadAction<{ newSession: CustomSession, authMethod: typeof CUSTOM_METHOD | typeof HOSTED_UI_REDIRECT }>) => {
      // Used after login or successful refresh
      state.session = action.payload.newSession;
      state.isAuthenticated = true;
      state.isAuthReady = true;
      state.authMethod = action.payload.authMethod
    },

    clearSessionRTK: (state) => {
      // Used after logout or failed refresh
      state.session = null;
      state.isAuthReady = true;
      state.isAuthenticated = false;
      state.authMethod = undefined
    },

    setAuthReady: (state, action: PayloadAction<boolean>) => {
      state.isAuthReady = action.payload;
    },
    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    setIsMagicLogin: (state, action: PayloadAction<boolean>) => {
      // Set once (typically true) by MagicLinkGuard when it sees ?t= in the URL
      // at first mount. Consumers gate magic-link-dependent setup on this.
      state.isMagicLogin = action.payload;
    }
  },

  // --- Extra Reducers (For Thunk Lifecycle) ---
  extraReducers: (builder) => {
    // These are optional, but provide hooks for global loading/error states
    builder
      .addCase(checkAndRefreshAccessToken.pending, (state) => {
        // state.isRefreshingToken = true;
      })
      .addCase(checkAndRefreshAccessToken.fulfilled, (state) => {
        // state.isRefreshingToken = false;
      })
      .addCase(checkAndRefreshAccessToken.rejected, (state) => {
        // state.isRefreshingToken = false;
      });
  },
});

// Export the actions and the reducer
export const { setSessionForRTK, clearSessionRTK, setAuthReady, setAuthenticated, setIsMagicLogin } = userTokenManagementSlice.actions;
export default userTokenManagementSlice.reducer;
export const selectStateRTK = (state: RootState) => state.userTokenManagement
export const selectSessionRTK = (state: RootState) => {
  return state.userTokenManagement.session
}
export const selectCustomSessionRTK = (state: RootState) => {
  const session = state.userTokenManagement.authMethod === CUSTOM_METHOD ? state.userTokenManagement.session : undefined
  return session
}
export const selectAuthMethodRTK = (state: RootState) => {
  return state.userTokenManagement.authMethod
}


export const selectIsAuthenticated = (state: RootState) => state.userTokenManagement.isAuthenticated
export const selectIsAuthReady = (state: RootState) => state.userTokenManagement.isAuthReady
export const selectAuthMethod = (state: RootState) => state.userTokenManagement.authMethod
export const selectIsMagicLogin = (state: RootState) => state.userTokenManagement.isMagicLogin